import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import {
  useV2CreateProjectCreditMutation,
  useV2UpdateProjectCreditMutation,
} from "@generated";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ControlledDropDown from "@components/Forms/ControlledDropDown";
import FormNumberField from "@components/Forms/FormNumberField";
import MultipleLineField from "@components/Forms/MulitpleLineField";
import { ProjectCredit, PaymentCreditOption } from "../types/ProjectCredit";
import useStyles from "../styles/useStyles";

interface Props {
  formId?: string;
  projectId: string;
  credit?: ProjectCredit;
  paymentCredits: PaymentCreditOption[];
}

const CreditForm = ({
  formId,
  projectId,
  credit,
  paymentCredits,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const styles = useStyles();

  const isEdit = !!credit;

  const [paymentCreditId, setPaymentCreditId] = useState<string | null>(
    credit?.paymentCreditId || null
  );
  const [amount, setAmount] = useState(credit ? String(credit.amount) : "0.0");
  const [criteria, setCriteria] = useState(credit?.criteria || "");

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const [createProjectCredit] = useV2CreateProjectCreditMutation();
  const [updateProjectCredit] = useV2UpdateProjectCreditMutation();

  useEffect(() => {
    if (isEdit && credit) {
      setPaymentCreditId(credit.paymentCreditId);
      setAmount(String(credit.amount));
      setCriteria(credit.criteria);
    }
  }, [credit]);

  const dropdownValue = (() => {
    if (!paymentCreditId) return "";
    const pc = paymentCredits.find((p) => p.id === paymentCreditId);
    return pc ? `${pc.paymentSource} / ${pc.description}` : "";
  })();

  const paymentCreditOptions = [
    ...(isEdit
      ? []
      : [
          <option value="" key="blank">
            Please select
          </option>,
        ]),
    ...paymentCredits.map((pc) => (
      <option accessKey={pc.id} key={pc.id}>
        {`${pc.paymentSource} / ${pc.description}`}
      </option>
    )),
  ];

  const saveCredit = async () => {
    if (!paymentCreditId) {
      setErrorMessages("Please select a payment credit");
      setShowFailAlert(true);
      return;
    }

    const payload = {
      data: {
        type: "project-credits" as const,
        ...(isEdit ? { id: Number(credit!.id) } : {}),
        attributes: {
          amount: parseFloat(amount),
        },
        relationships: {
          project: { data: { type: "projects", id: projectId } },
          "payment-credit": {
            data: { type: "payment-credits", id: paymentCreditId },
          },
        },
      },
    };

    try {
      if (isEdit) {
        await updateProjectCredit({
          id: String(credit!.id),
          body: payload as any,
        }).unwrap();
        navigate(`/projects/${projectId}/project_credits`, {
          state: { edit: true },
        });
      } else {
        await createProjectCredit({
          body: payload as any,
        }).unwrap();
        navigate(`/projects/${projectId}/project_credits`, {
          state: { new: true },
        });
      }
    } catch (err: any) {
      const message =
        err?.data?.errors?.[0]?.detail ||
        "Something went wrong. Your changes could not be saved.";
      setErrorMessages(message);
      setShowFailAlert(true);
    }
  };

  return (
    <>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          saveCredit();
        }}
      >
        <Row>
          <Col>
            <ControlledDropDown
              label="Payment Credit *"
              required={true}
              options={paymentCreditOptions}
              value={dropdownValue}
              onChange={(e: any) => {
                const selected = e.target.options.selectedIndex;
                const selectedId = e.target.options[selected].accessKey;
                setPaymentCreditId(selectedId || null);
                const pc = paymentCredits.find((p) => p.id === selectedId);
                setCriteria(pc?.criteria || "");
              }}
              defaultValue={undefined}
              readOnly={false}
              disabled={false}
              fieldFooter={undefined}
            />
          </Col>
          <Col>
            <FormNumberField
              label="Amount *"
              required={true}
              step="1.0"
              min="0.0"
              value={amount}
              onChange={(e: any) => setAmount(e.target.value)}
              placeholder={undefined}
              max={undefined}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <MultipleLineField
              rows={5}
              label="Criteria"
              value={criteria}
              disabled={true}
              id={undefined}
              placeholder={undefined}
              defaultValue={undefined}
              onChange={undefined}
              onBlur={undefined}
              required={false}
            />
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default CreditForm;
